import _ from 'lodash';
import { Button, Col, Form, Input, Modal, Row } from '@duxton-web/ui';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CREATE_UCD_PROJECT, createUCDProject } from 'omega-rtc/actions/ucd';
import CustomizeRequiredMark from 'omega-rtc/components/custom/CustomizeRequiredMark';
import {
  INTERNAL_STATUS,
  PROJECT_FORM_KEYS,
  UCD_ROUTES_LINK,
} from 'omega-rtc/constants/ucd';
import {
  generateProjectCode,
  getRequestStateKey,
} from 'omega-rtc/utils/ucd-utils';
import {
  hasJustSuccess,
  isRequestsPending,
} from 'omega-rtc/utils/request-state-utils';
import { snackBar } from 'omega-rtc/components/custom/SnackBar';
import CountrySelector from 'omega-rtc/components/custom/entities/CountrySelector';
import AudienceTypeSelector from 'omega-rtc/components/custom/entities/AudienceTypeSelector';
import BusinessObjectiveSelector from 'omega-rtc/components/custom/entities/BusinessObjectiveSelector';

const AddNewProjectModal = ({ open, onCancel, onSubmit, router }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const user = useSelector((state) => state.app.user);
  const countries = useSelector((state) => state.countries.entities);
  const createUcdProjectRequest = useSelector((state) =>
    _.get(state.requests, getRequestStateKey(CREATE_UCD_PROJECT), {}),
  );
  const createUcdProjectRef = useRef();

  const newProjectID = useSelector((state) => state.ucd.newProjectID);

  useEffect(() => {
    createUcdProjectRef.current = createUcdProjectRequest;
  }, []);
  const isUpdating = isRequestsPending(createUcdProjectRequest);

  useEffect(() => {
    if (isUpdating) {
      createUcdProjectRef.current = createUcdProjectRequest;
    }
  }, [isUpdating, createUcdProjectRequest]);

  useEffect(() => {
    if (hasJustSuccess(createUcdProjectRef.current, createUcdProjectRequest)) {
      onSubmit();
      snackBar.success({
        content: `${form.getFieldValue(PROJECT_FORM_KEYS.NAME)} has been created successfully`,
      });
      router.push(UCD_ROUTES_LINK.PROJECT_VIEW(newProjectID));
      createUcdProjectRef.current = createUcdProjectRequest;
    }
  }, [createUcdProjectRequest]);

  const handleSaveAsDraftProject = () => {
    validateData();
    createProject(INTERNAL_STATUS.draft);
  };
  const handlePublishProject = () => {
    validateData();
    createProject(INTERNAL_STATUS.active);
  };

  const createProject = async (status) => {
    const data = form.getFieldsValue();
    const { audience, objective, name, shortName, country } = data;

    const chosenCountry = countries.find(
      (globalCountry) => globalCountry.countryID === country,
    );

    const projectCode = generateProjectCode({
      countryCode: chosenCountry.code,
      year: new Date().getFullYear(),
      audience,
      objective,
      shortName,
    });

    const payload = {
      project: {
        audience,
        objective,
        name,
        shortName,
        code: projectCode,
        countryID: country,
        timezone: chosenCountry.timezones[0].tz,
      },
      status,
      user: user.email,
    };
    try {
      dispatch(createUCDProject(payload));
      createUcdProjectRef.current = createUcdProjectRequest;
    } catch (error) {
      console.log(error);
    }
  };

  const validateData = async () => {
    try {
      await form.validateFields();
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Modal
      title="Add New Project"
      centered
      width={720}
      open={open}
      onCancel={onCancel}
      footer={[
        <Button
          key="save-as-draft"
          onClick={handleSaveAsDraftProject}
          type="text"
          loading={isUpdating}
        >
          Save as Draft
        </Button>,
        <Button
          key="publish"
          onClick={handlePublishProject}
          type="primary"
          loading={isUpdating}
        >
          Publish
        </Button>,
      ]}
    >
      <Form
        layout="vertical"
        form={form}
        style={{ marginTop: 24 }}
        requiredMark={CustomizeRequiredMark}
      >
        <Row gutter={[24, 0]}>
          <Col span={12}>
            <Form.Item
              label="Audience Type"
              required
              name={PROJECT_FORM_KEYS.AUDIENCE}
              rules={[
                { required: true, message: 'Please select your Audience!' },
              ]}
            >
              <AudienceTypeSelector />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Business Objective"
              required
              name={PROJECT_FORM_KEYS.OBJECTIVE}
              rules={[
                { required: true, message: 'Please select your objective' },
              ]}
            >
              <BusinessObjectiveSelector />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Project Description"
              name={PROJECT_FORM_KEYS.NAME}
              required
              rules={[
                { required: true, message: 'Please enter project description' },
              ]}
            >
              <Input placeholder="Please enter project description" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Project Name"
              name={PROJECT_FORM_KEYS.SHORT_NAME}
              rules={[{ required: true, message: 'Please enter short name' }]}
            >
              <Input placeholder="Ex. ONEGRABPRO" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Country"
              name={PROJECT_FORM_KEYS.COUNTRY}
              required
              rules={[{ required: true, message: 'Please select Country' }]}
            >
              <CountrySelector />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default AddNewProjectModal;
